import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '@/App';
import { Settings as SettingsIcon, Check, X as XIcon } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { WIDGET_REGISTRY } from '@/components/dashboard/widgetRegistry';
import SortableWidget from '@/components/dashboard/SortableWidget';
import DashboardCustomizer from '@/components/dashboard/DashboardCustomizer';
import { toast } from 'sonner';

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [budgetAlerts, setBudgetAlerts] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customizing, setCustomizing] = useState(false);
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchAll = useCallback(async () => {
    try {
      const [summaryRes, trendsRes, txnRes, budgetRes, prefRes, categoryRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`),
        axios.get(`${API}/analytics/trends`),
        axios.get(`${API}/transactions`),
        axios.get(`${API}/budgets/status`),
        axios.get(`${API}/dashboard/preferences`),
        axios.get(`${API}/analytics/by-category?type=expense`),
      ]);
      setSummary(summaryRes.data);
      setTrends(trendsRes.data.slice(-6));
      setRecentTransactions(txnRes.data.slice(0, 5));
      setBudgetAlerts((budgetRes.data || []).filter((b) => b.status === 'warning' || b.status === 'over'));
      setWidgets(prefRes.data.widgets || []);
      const sorted = [...(categoryRes.data || [])].sort((a, b) => b.amount - a.amount);
      setTopCategories(sorted);
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const widgetData = useMemo(
    () => ({ summary, trends, recentTransactions, budgetAlerts, topCategories }),
    [summary, trends, recentTransactions, budgetAlerts, topCategories]
  );

  const visibleWidgets = useMemo(
    () => widgets.filter((w) => w.enabled && WIDGET_REGISTRY[w.id]),
    [widgets]
  );

  const persistWidgets = async (next) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/dashboard/preferences`, { widgets: next });
      setWidgets(res.data.widgets || next);
    } catch (e) {
      console.error('Failed to save layout', e);
      toast.error('Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const visibleIds = visibleWidgets.map((w) => w.id);
    const oldIdx = visibleIds.indexOf(active.id);
    const newIdx = visibleIds.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const newVisibleOrder = arrayMove(visibleIds, oldIdx, newIdx);
    // Rebuild full widgets list: first the reordered enabled widgets, then disabled tail
    const enabledMap = new Map(widgets.filter((w) => w.enabled).map((w) => [w.id, w]));
    const disabled = widgets.filter((w) => !w.enabled);
    const next = [...newVisibleOrder.map((id) => enabledMap.get(id)), ...disabled];
    setWidgets(next);
  };

  const handleRemove = (id) => {
    const next = widgets.map((w) => (w.id === id ? { ...w, enabled: false } : w));
    setWidgets(next);
  };

  const handleAdd = (id) => {
    const entry = WIDGET_REGISTRY[id];
    const existing = widgets.find((w) => w.id === id);
    if (existing) {
      setWidgets(widgets.map((w) => (w.id === id ? { ...w, enabled: true } : w)));
    } else {
      setWidgets([...widgets, { id, enabled: true, size: entry?.defaultSize || 'medium' }]);
    }
  };

  const handleSave = async () => {
    await persistWidgets(widgets);
    setCustomizing(false);
    setCustomizerOpen(false);
    toast.success('Dashboard layout saved');
  };

  const handleCancel = async () => {
    setCustomizing(false);
    setCustomizerOpen(false);
    // Re-fetch original layout
    try {
      const res = await axios.get(`${API}/dashboard/preferences`);
      setWidgets(res.data.widgets || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/dashboard/preferences/reset`);
      setWidgets(res.data.widgets || []);
      toast.success('Dashboard restored to default');
      setCustomizerOpen(false);
      setCustomizing(false);
    } catch (e) {
      toast.error('Failed to reset layout');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 md:p-12" data-testid="dashboard-loading">
        <div className="text-slate-900 font-chivo text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-8 md:p-12" data-testid="dashboard">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="dashboard-title">
              Dashboard
            </h1>
            <p className="text-slate-600 mt-1 font-ibm">Financial overview and key metrics</p>
          </div>
          <div className="flex items-center gap-2">
            {!customizing ? (
              <button
                type="button"
                onClick={() => {
                  setCustomizing(true);
                }}
                className="flex items-center gap-2 px-4 py-2 border border-slate-900 text-slate-900 text-xs font-ibm font-semibold uppercase tracking-widest rounded-sm hover:bg-slate-900 hover:text-white transition-colors"
                data-testid="customize-dashboard-btn"
              >
                <SettingsIcon size={14} />
                Customize
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setCustomizerOpen(true)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 text-xs font-ibm font-semibold uppercase tracking-widest rounded-sm hover:bg-slate-50 transition-colors"
                  data-testid="open-customizer-btn"
                >
                  Add Widgets
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-xs font-ibm font-semibold uppercase tracking-widest rounded-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
                  data-testid="cancel-customize-btn"
                >
                  <XIcon size={14} />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-xs font-ibm font-semibold uppercase tracking-widest rounded-sm hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  data-testid="save-customize-btn"
                >
                  <Check size={14} />
                  {saving ? 'Saving...' : 'Save Layout'}
                </button>
              </>
            )}
          </div>
        </div>

        {visibleWidgets.length === 0 ? (
          <div
            className="border border-dashed border-slate-300 rounded-sm p-12 text-center"
            data-testid="empty-dashboard"
          >
            <p className="font-ibm text-slate-600">Your dashboard is empty. Click <strong>Customize</strong> to add widgets.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={visibleWidgets.map((w) => w.id)} strategy={rectSortingStrategy}>
              <div
                className="grid grid-cols-1 md:grid-cols-4 gap-4 items-stretch"
                data-testid="widgets-grid"
              >
                {visibleWidgets.map((w) => (
                  <SortableWidget
                    key={w.id}
                    widget={w}
                    data={widgetData}
                    customizing={customizing}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </motion.div>

      <DashboardCustomizer
        open={customizerOpen}
        onClose={() => setCustomizerOpen(false)}
        widgets={widgets}
        onAddWidget={handleAdd}
        onReset={handleReset}
      />
    </div>
  );
};

export default Dashboard;
